import {
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
} from '@/services/notificationService';

interface QueuedMutation {
  id: string;
  type: 'markAllAsRead' | 'deleteNotification' | 'clearAll';
  payload?: { id: string };
  timestamp: number;
}

const STORAGE_KEY = 'offline-mutation-queue';

function getQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: QueuedMutation[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

export function enqueue(mutation: Omit<QueuedMutation, 'id' | 'timestamp'>): void {
  const queue = getQueue();
  queue.push({
    ...mutation,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    timestamp: Date.now(),
  });
  saveQueue(queue);
}

function dequeue(id: string): void {
  const queue = getQueue();
  saveQueue(queue.filter(m => m.id !== id));
}

async function executeMutation(mutation: QueuedMutation): Promise<void> {
  switch (mutation.type) {
    case 'markAllAsRead':
      await markAllAsRead();
      break;
    case 'deleteNotification':
      if (mutation.payload?.id) {
        await deleteNotification(mutation.payload.id);
      }
      break;
    case 'clearAll':
      await clearAllNotifications();
      break;
  }
}

export async function processQueue(): Promise<void> {
  const queue = getQueue();
  if (queue.length === 0) return;

  for (const mutation of queue) {
    try {
      await executeMutation(mutation);
      dequeue(mutation.id);
    } catch (error: any) {
      // 404/410 = resource already gone, safe to dequeue
      if (error?.response?.status === 404 || error?.response?.status === 410) {
        dequeue(mutation.id);
        continue;
      }
      // Other errors: stop processing, will retry next time we come online
      break;
    }
  }
}

let listening = false;

export function startListening(): void {
  if (listening) return;
  listening = true;

  window.addEventListener('online', () => {
    processQueue();
  });
}
